import { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import ModalDrawer from 'components/microLayouts/drawer';
import Button from 'components/button';
import { format } from 'date-fns';
import InputText from 'pages/Reporting/components/InputText';
import { onDownloadReport } from 'pages/Reporting/components/shared/utils';
import ReportMobileListItem from 'pages/Reporting/components/shared/ReportMobileListItem';
import { IReportSchedule } from 'contexts/reporting/types';
import { getPopularReportImg } from 'utils/imageHandling';
import FrequencyDropdown, {
  getLabelFromValue,
} from 'pages/Reporting/components/ReportingScheduled/FrequencyDropdown';
import { reportKeys } from 'pages/Reporting/components/ReportingScheduled/ScheduledReportsTable';
import PauseSwitchCell from 'pages/Reporting/components/ReportingScheduled/PauseSwitchCell';
import { AppContext } from 'contexts/app';
import {
  CUSTOM_REPORT_SCHEDULING_BLOCKED_COPY,
  SCHEDULED_REPORT_NA_VALUE,
} from 'constants/reporting';

const ReportMobileDrawerContainer = styled.div(
  ({ theme }) => `
    height: 100%;
    background: ${theme.default.colors.palette.Business_LightThemeGreyLighter};
    .drawer {
      &__header {
        margin-top: 10px;
        background: var(--secondary-promo-purple-gradient);
        padding: 25px 10px;

        h5 {
          font-size: 20px;
          font-style: normal;
          font-weight: 700;
          line-height: 26px;
          color: white;
        }

        .report-subtitle {
          display: block;
          color: rgba(255, 255, 255, 0.8);
          font-feature-settings: 'liga' off, 'clig' off;
          font-family: Lato;
          font-size: 12px;
          font-style: normal;
          font-weight: 400;
          line-height: 14px; /* 116.667% */
        }
      }

      &__body {
        padding: 15px;
        height: 100%;
        display: flex;
        flex-direction: column;

        .form-group {
          display: flex;
          gap: 1px;
          margin-top: 5px;

          > * {
            display: flex;
            align-items: center;
            height: 54px;
            background: white;
            padding: 10px;
            font-size: 14px;
            font-style: normal;
          }

          &-label {
            width: 40%;
            color: ${theme.default.colors.palette.Business_LightThemeGreyDarker};
            font-weight: 500;

            &--editable {
              padding-right: 30px;
            }
          }

          &-value {
            width: 60%;
            font-weight: 700;

            &--read-only {
              padding-left: 20px;
            }

            input {
              font-size: inherit;
              font-style: inherit;
              font-weight: inherit;
              background: none;
              white-space: inherit;
              overflow: inherit;
              text-overflow: inherit;
            }
          }
        }
      }

      &__actions {
        display: flex;
        gap: 10px;
        position: fixed;
        bottom: 80px;
        left: 15px;
        right: 15px;   
        background: ${theme.default.colors.palette.Business_LightThemeGreyLighter};
        button {
          width: 50%;
        } 
      }
    }
  `
);

const ReportMobileListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 20px;
`;

interface ScheduledReportsMobileListProps {
  reports: IReportSchedule[];
  loading: boolean;
  onDeleteReport: (id: string, name: string) => void;
  onUpdateReport: (id: string, key: string, value: string | boolean) => Promise<void>;
  onClose: () => void;
  readOnly?: boolean;
}

const ScheduledReportsMobileList: React.FC<ScheduledReportsMobileListProps> = ({
  reports,
  loading,
  onDeleteReport,
  onUpdateReport,
  onClose,
  readOnly = false,
}) => {
  const { setBackButton } = useContext(AppContext);
  const [reportDetails, setReportDetails] = useState(null);

  useEffect(() => {
    if (!reportDetails) {
      setBackButton({
        label: 'Go Back',
        action: () => {
          setBackButton(null);
          onClose();
        },
      });
    }
  }, [reportDetails]);

  const onReportDetail = (report) => {
    setReportDetails(report);
    setBackButton({
      label: 'Manage my scheduled reports',
      action: () => setReportDetails(null),
    });
  };

  const handleUpdate = async (id, key, value) => {
    await onUpdateReport(id, key, value);
    setReportDetails((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <>
      <ReportMobileListContainer
        data-testid="report-mobile-list-container"
        style={{ opacity: loading ? 0.5 : 1 }}
      >
        {reports.map((report) => {
          const isBlockedRow = readOnly && !report.is_popular_report;
          const label = (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
              {report.is_popular_report && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: 'rgba(167, 147, 246, 0.12)',
                  }}
                >
                  <img
                    src={getPopularReportImg(report.type)}
                    alt={`${report.type} popular report`}
                    width={18}
                    height={18}
                    style={{ display: 'inline-block' }}
                  />
                </span>
              )}
              <span>{report.name}</span>
            </span>
          );

          const dr = (report as any)?.date_range || (report as any)?.date_rage || {};
          const fromStr = dr?.from ?? dr?.from_date ?? dr?.start ?? dr?.start_date ?? dr?.fromDate;
          const toStr = dr?.to ?? dr?.to_date ?? dr?.end ?? dr?.end_date ?? dr?.toDate;
          const parts: string[] = [];
          if (report.frequency) parts.push(getLabelFromValue(report.frequency));
          if (fromStr && toStr) {
            parts.push(
              `${format(new Date(fromStr), 'yyyy-MM-dd')} to ${format(
                new Date(toStr),
                'yyyy-MM-dd'
              )}`
            );
          }
          // Blocked rows replace the frequency/date-range summary with the upgrade copy
          // (intentional — mirrors the desktop name column, not a dropped summary).
          const subLabel = isBlockedRow
            ? CUSTOM_REPORT_SCHEDULING_BLOCKED_COPY
            : parts.length
            ? parts.join(': ')
            : undefined;

          return (
            <ReportMobileListItem
              key={report.id}
              label={label}
              subLabel={subLabel}
              onReportDetail={() => onReportDetail(report)}
              data-testid={`report-item-${report.id}`}
            />
          );
        })}
      </ReportMobileListContainer>
      <ModalDrawer
        closable={false}
        placement="right"
        visible={!!reportDetails}
        destroyOnClose
        zIndex={100}
        width="100%"
        contentWrapperStyle={{ filter: loading ? 'blur(1px)' : 'none' }}
        bodyStyle={{ padding: '64px 0 70px' }}
        data-testid="modal-drawer"
      >
        {reportDetails &&
          (() => {
            const isBlockedRow = readOnly && !reportDetails.is_popular_report;
            return (
              <ReportMobileDrawerContainer>
                <div className="drawer__header">
                  <h5>{reportDetails.name}</h5>
                  {isBlockedRow && (
                    <span className="report-subtitle">{CUSTOM_REPORT_SCHEDULING_BLOCKED_COPY}</span>
                  )}
                </div>
                <div className="drawer__body">
                  <div className="form-group">
                    <label className="form-group-label form-group-label--editable">
                      Report name
                    </label>
                    <div className="form-group-value">
                      {reportDetails.is_popular_report ? (
                        <span>{reportDetails.name}</span>
                      ) : (
                        <InputText
                          value={reportDetails.name}
                          disabled={loading}
                          editIconOutside
                          onSave={(value) =>
                            handleUpdate(reportDetails.id, reportKeys.NAME.key, value)
                          }
                        />
                      )}
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-group-label">Created</label>
                    <div className="form-group-value form-group-value--read-only">
                      <span>
                        {format(new Date(reportDetails.created_at), 'HH:mmaa yyyy-MM-dd')}
                      </span>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-group-label form-group-label--editable">Frequency</label>
                    <div className="form-group-value">
                      {isBlockedRow ? (
                        <span>{SCHEDULED_REPORT_NA_VALUE}</span>
                      ) : (
                        <FrequencyDropdown
                          value={reportDetails.frequency}
                          width="100%"
                          disabled={loading}
                          editIconOutside
                          onChange={(newValue) =>
                            handleUpdate(reportDetails.id, reportKeys.FREQUENCY.key, newValue)
                          }
                        />
                      )}
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-group-label">Schedule status</label>
                    <div className="form-group-value">
                      {isBlockedRow ? (
                        <span>{SCHEDULED_REPORT_NA_VALUE}</span>
                      ) : (
                        <PauseSwitchCell
                          isPaused={reportDetails.is_paused}
                          record={reportDetails}
                          loading={loading}
                          onChange={(newValue) =>
                            handleUpdate(reportDetails.id, reportKeys.IS_PAUSED.key, newValue)
                          }
                        />
                      )}
                    </div>
                  </div>
                </div>
                <div className="drawer__actions">
                  <Button
                    $secondary
                    onClick={() => onDeleteReport(reportDetails.id, reportDetails.name)}
                  >
                    Remove report
                  </Button>
                  <Button $primary onClick={() => onDownloadReport(reportDetails.id)}>
                    Download report
                  </Button>
                </div>
              </ReportMobileDrawerContainer>
            );
          })()}
      </ModalDrawer>
    </>
  );
};

export default ScheduledReportsMobileList;
